import * as React from 'react';
import { CheckCircle } from 'lucide-react';
import { Alert } from './Alert';
import { Badge } from './Badge';
import { Grid, Inline, Stack } from './Layout';
import { List, ListItem } from './List';
import { OptionCard } from './OptionCard';
import { Text } from './Text';

export interface PlanPickerPlan {
  id: string;
  name: string;
  description: string;
  /** Whole currency units, already divided down from cents. Null or 0 reads "Free". */
  price: number | null;
  /** The suffix after the figure — "/month". */
  period: string;
  features: string[];
  popular?: boolean;
}

export interface PlanPickerProps {
  /** In the order they should appear; the container sorts. */
  plans: PlanPickerPlan[];
  /** The chosen plan's id. Empty, and the prompt to pick one shows. */
  selectedId: string;
  onSelect: (id: string) => void;
}

/**
 * The onboarding pricing step: one wide OptionCard per plan, name and blurb
 * and features on the left, the price on the right. Nothing chosen yet shows
 * the prompt above the list; an empty list — the plans have not arrived, or
 * failed to — shows nothing but the prompt.
 *
 *   <PlanPicker plans={plans} selectedId={formData.selectedPlan}
 *     onSelect={(id) => updateFormData('selectedPlan', id)} />
 */
export function PlanPicker({ plans, selectedId, onSelect }: PlanPickerProps) {
  return (
    <Stack gap={8}>
      {selectedId.trim() === '' && (
        <Alert tone="destructive">
          Please select a pricing plan to continue
        </Alert>
      )}
      <Grid gap={6}>
        {plans.map((plan) => (
          <OptionCard
            key={plan.id}
            as="div"
            data-testid="pricing-card"
            selected={selectedId === plan.id}
            onSelect={() => onSelect(plan.id)}
          >
            <Inline gap={6} align="center" justify="between">
              <Stack gap={3} grow>
                <Inline gap={3} align="center">
                  <Text variant="h3">{plan.name}</Text>
                  {plan.popular && (
                    <Badge tone="info" appearance="subtle">
                      Most Popular
                    </Badge>
                  )}
                </Inline>
                <Text tone="muted">{plan.description}</Text>
                <List marker="check" tone="muted" gap={2}>
                  {plan.features.map((feature, index) => (
                    <ListItem key={index} icon={CheckCircle}>
                      {feature}
                    </ListItem>
                  ))}
                </List>
              </Stack>
              <Text variant="h1" as="p" align="right" numeric>
                {plan.price != null && plan.price > 0 ? (
                  <>
                    ${plan.price}
                    <Text variant="body-lg" tone="muted" as="span">
                      {plan.period}
                    </Text>
                  </>
                ) : (
                  'Free'
                )}
              </Text>
            </Inline>
          </OptionCard>
        ))}
      </Grid>
    </Stack>
  );
}
